import {
  type BookkeepingProgressionInfo as BookkeepingProgressionInfoProto,
  type WorkItemCompletion as WorkItemCompletionProto,
  type BookkeepingCycle as BookkeepingCycleProto,
  type CycleCorrection as CycleCorrectionProto,
  type PlatformConnection as PlatformConnectionProto,
  type WorkItem as WorkItemProto,
  BookkeepingSoftware as BookkeepingSoftwareProto,
  BookkeepingStage as BookkeepingStageProto,
  AccessStatus as AccessStatusProto,
  BookkeepingSoftwareType as BookkeepingSoftwareTypeProto,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { safeTimestampToDate } from '@/utils/grpc-util'
import { match } from '@/utils/pattern-match-util'
import type {
  BookkeepingCycle,
  BookkeepingCycleCorrection,
  BookkeepingCycleStatus,
  BookkeepingProgressionInfoProps,
  ProvidedPlatformConnection,
  WorkItemCompletion,
  WorkItemProps,
} from './BookkeepingProgressionInfo'
import type { BookkeepingStage } from './bookkeeping-stage'
import type { BookkeepingAccessStatus } from './bookkeeping-access-status'
import type { BookkeepingSoftware } from './bookkeeping-software'

export function mapBookkeepingProgressionInfoProtoToProps(
  proto: BookkeepingProgressionInfoProto,
): Partial<BookkeepingProgressionInfoProps> {
  const platformConnection = proto.getPlatformConnection()
  const bookkeepingSoftware = proto.getBookkeepingSoftware()

  // Unspecified enum values leave the current value in place.
  return {
    id: proto.getId(),
    stage: proto.getStage()
      ? mapBookkeepingStageProtoToBookkeepingStage(proto.getStage())
      : undefined,
    accessStatus: proto.getAccessStatus()
      ? mapAccessStatusProtoToAccessStatus(proto.getAccessStatus())
      : undefined,
    workItems: proto.getWorkItemsList().map(mapWorkItemProtoToProps),
    cycles: proto
      .getCyclesList()
      .map(mapBookkeepingCycleProtoToBookkeepingCycle),
    platformConnection: platformConnection
      ? mapPlatformConnectionProtoToPlatformConnection(platformConnection)
      : null,
    bookkeepingSoftware: bookkeepingSoftware
      ? mapBookkeepingSoftwareProtoToBookkeepingSoftware(bookkeepingSoftware)
      : null,
  }
}

/**
 * Maps the proto representation of the bookkeeping software to its model,
 * if the client picked one.
 */
export function mapBookkeepingSoftwareProtoToBookkeepingSoftware(
  proto: BookkeepingSoftwareProto,
): BookkeepingSoftware | null {
  return match(proto.getType(), {
    [BookkeepingSoftwareTypeProto.QUICK_BOOKS]: (): BookkeepingSoftware => ({
      type: 'QuickBooks',
    }),
    [BookkeepingSoftwareTypeProto.XERO]: (): BookkeepingSoftware => ({
      type: 'Xero',
    }),
    [BookkeepingSoftwareTypeProto.OTHER]: (): BookkeepingSoftware => ({
      type: 'Other',
      name: proto.getOtherName(),
    }),
    [BookkeepingSoftwareTypeProto.BOOKKEEPING_SOFTWARE_TYPE_UNSPECIFIED]: null,
  })
}

/**
 * Maps a {@link BookkeepingSoftware} to its proto representation.
 */
export function mapBookkeepingSoftwareToProto(software: BookkeepingSoftware) {
  const proto = new BookkeepingSoftwareProto()

  switch (software.type) {
    case 'QuickBooks':
      return proto.setType(BookkeepingSoftwareTypeProto.QUICK_BOOKS)
    case 'Xero':
      return proto.setType(BookkeepingSoftwareTypeProto.XERO)
    case 'Other':
      return proto
        .setType(BookkeepingSoftwareTypeProto.OTHER)
        .setOtherName(software.name)
  }
}

// The progression info only surfaces managed connections.
function mapPlatformConnectionProtoToPlatformConnection(
  proto: PlatformConnectionProto,
): ProvidedPlatformConnection | null {
  const quickBooks = proto.getQuickBooks()
  if (quickBooks) {
    return {
      type: 'QuickBooks',
      quickBooks: {
        connectionId: quickBooks.getConnectionId(),
        realmId: quickBooks.getRealmId(),
        companyName: quickBooks.getCompanyName(),
      },
    }
  }

  const xero = proto.getXero()
  if (xero) {
    return {
      type: 'Xero',
      xero: {
        connectionId: xero.getConnectionId(),
        tenantId: xero.getTenantId(),
        tenantName: xero.getTenantName(),
      },
    }
  }

  return null
}

function mapBookkeepingCycleProtoToBookkeepingCycle(
  proto: BookkeepingCycleProto,
): BookkeepingCycle {
  const status = proto.getStatus()
  const correction = proto.getCorrection()
  return {
    id: proto.getId(),
    number: proto.getNumber(),
    startedAt: safeTimestampToDate(proto.getStartedAt()),
    status: status
      ? mapBookkeepingCycleStatusProtoToBookkeepingCycleStatus(status)
      : { type: 'Unknown' },
    correction: correction
      ? mapCycleCorrectionProtoToBookkeepingCycleCorrection(correction)
      : null,
  }
}

function mapBookkeepingCycleStatusProtoToBookkeepingCycleStatus(
  proto: BookkeepingCycleProto.CycleStatus,
): BookkeepingCycleStatus {
  if (proto.getInProgress()) {
    return { type: 'InProgress' }
  }

  const canceled = proto.getCanceled()
  if (canceled) {
    return {
      type: 'Canceled',
      canceledAt: safeTimestampToDate(canceled.getCanceledAt()),
    }
  }

  const completed = proto.getCompleted()
  if (completed) {
    return {
      type: 'Completed',
      completedAt: safeTimestampToDate(completed.getCompletedAt()),
      completedById: completed.getCompletedById(),
      items: completed
        .getItemsList()
        .map(mapWorkItemCompletionProtoToWorkItemCompletion),
    }
  }

  return { type: 'Unknown' }
}

function mapCycleCorrectionProtoToBookkeepingCycleCorrection(
  proto: CycleCorrectionProto,
): BookkeepingCycleCorrection {
  return {
    correctedAt: safeTimestampToDate(proto.getCorrectedAt()),
    correctedById: proto.getCorrectedById(),
    items: proto
      .getItemsList()
      .map(mapWorkItemCompletionProtoToWorkItemCompletion),
  }
}

function mapWorkItemCompletionProtoToWorkItemCompletion(
  proto: WorkItemCompletionProto,
): WorkItemCompletion {
  return {
    id: proto.getId(),
    completedCount: proto.getCompletedCount(),
    name: proto.getName(),
    description: proto.getDescription(),
  }
}

function mapWorkItemProtoToProps(proto: WorkItemProto): WorkItemProps {
  return {
    workItemId: proto.getWorkItemId(),
    inventoryItemId: proto.getInventoryItemId(),
    inventoryItemName: proto.getInventoryItemName(),
    inventoryItemDescription: proto.getInventoryItemDescription(),
    quantity: proto.getQuantity(),
    completedCount: proto.getCompletedCount(),
  }
}

function mapBookkeepingStageProtoToBookkeepingStage(
  proto: BookkeepingStageProto,
): BookkeepingStage {
  return match(proto, {
    [BookkeepingStageProto.BOOKKEEPING_STAGE_UNSPECIFIED]:
      'BookkeepingStageUnspecified',
    [BookkeepingStageProto.PENDING]: 'Pending',
    [BookkeepingStageProto.PROVISIONING_ACCESS]: 'ProvisioningAccess',
    [BookkeepingStageProto.ASSESSING_BOOKS]: 'AssessingBooks',
    [BookkeepingStageProto.READY_TO_START]: 'ReadyToStart',
    [BookkeepingStageProto.CYCLE_IN_PROGRESS]: 'CycleInProgress',
    [BookkeepingStageProto.SCOPE_EXTENSION_REQUESTED]:
      'ScopeExtensionRequested',
    [BookkeepingStageProto.COMPLETED]: 'Completed',
  })
}

function mapAccessStatusProtoToAccessStatus(
  proto: AccessStatusProto,
): BookkeepingAccessStatus {
  return match(proto, {
    [AccessStatusProto.ACCESS_STATUS_UNSPECIFIED]: 'AccessStatusUnspecified',
    [AccessStatusProto.NOT_REQUESTED]: 'NotRequested',
    [AccessStatusProto.MANAGED_ACCESS_CLIENT_CONNECTION_REQUESTED]:
      'ManagedAccessClientConnectionRequested',
    [AccessStatusProto.MANAGED_ACCESS_CLIENT_CONFIRMATION_REQUESTED]:
      'ManagedAccessClientConfirmationRequested',
    [AccessStatusProto.MANAGED_ACCESS_ADMIN_CONFIRMATION_REQUESTED]:
      'ManagedAccessAdminConfirmationRequested',
    [AccessStatusProto.MANAGED_ACCESS_CONFIRMED]: 'ManagedAccessConfirmed',
    [AccessStatusProto.UNMANAGED_ACCESS_PROVIDER_CONFIRMATION_REQUESTED]:
      'UnmanagedAccessProviderConfirmationRequested',
    [AccessStatusProto.UNMANAGED_ACCESS_CONFIRMED]: 'UnmanagedAccessConfirmed',
    [AccessStatusProto.UNMANAGED_ACCESS_CLIENT_CONFIRMATION_REQUESTED]:
      'UnmanagedAccessClientConfirmationRequested',
  })
}
